import { forwardRef, RefObject, useEffect } from 'react';

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightElement,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  CheckOutline,
  DropdownInput,
  CrossOutline,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import { useCreatePayCalendarStore } from 'app/store';

import { payCalendarAslanDetailsFormSchema } from 'app/utils/schemas';

import {
  getOdpAllowancePercentagePercentageOptions,
  getStartingBalanceDaysOptions,
  getGlobalAllowancePercentageOptions,
  getAccrualPeriodOptions,
  getDeductionsConfigOptions,
} from 'app/components/PayCalendars/options';

import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useAuthContext from 'app/hooks/useAuthContext';

import { useParams } from 'react-router-dom';

import {
  PayCalendarAslanDetailsFormFields,
  PayCalendarDefineCalendarFormFields,
  PayCalendarFormProps,
} from '../types';

const PayCalendarAslanDetailsForm = forwardRef<HTMLElement, PayCalendarFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const payCalendarData = useCreatePayCalendarStore((state) => state.payCalendarData);
    const setPayCalendarData = useCreatePayCalendarStore((state) => state.setPayCalendarData);

    const { companyId } = useParams();
    const { user } = useAuthContext();

    const { data: company } = useFetchCompany(companyId || user?.companyId);

    const { data: companyConfiguration } = useFetchCompanyConfiguration(company?.id || '');

    const { payFrequency } = payCalendarData as PayCalendarDefineCalendarFormFields; // pay frequency determines the cut off options

    const isReserveEnabled = companyConfiguration?.modulesConfig.modules.RESERVE === 'ENABLED';
    const isOnDemandPayEnabled =
      companyConfiguration?.modulesConfig.modules.ON_DEMAND_PAY === 'ENABLED';

    // Debug logging removed for production

    const schema = payCalendarAslanDetailsFormSchema(isReserveEnabled, isOnDemandPayEnabled);

    const {
      register,
      control,
      watch,
      handleSubmit,
      setValue,
      setFocus,
      getValues,
      trigger,
      formState: { errors, isValid },
    } = useForm<PayCalendarAslanDetailsFormFields>({
      mode: 'onChange',
      defaultValues: payCalendarData as PayCalendarAslanDetailsFormFields,
      resolver: yupResolver(schema),
    });

    // watchers
    const watchOdpAllowancePercentage = watch('odpAllowancePercentage');
    const watchCustomOdpAllowancePercentage = watch('customOdpAllowancePercentage');
    const watchGlobalAllowancePercentage = watch('globalAllowancePercentage');
    const watchCustomGlobalAllowancePercentage = watch('customGlobalAllowancePercentage');

    // populate optional choices
    const odpAllowancePercentageOptions = getOdpAllowancePercentagePercentageOptions();
    const startingBalanceDaysOptions = getStartingBalanceDaysOptions(payFrequency || 'MONTHLY');
    const globalAllowancePercentageOptions = getGlobalAllowancePercentageOptions();
    const accrualPeriodOptions = getAccrualPeriodOptions();
    const madeAvailableTimeOptions = getDeductionsConfigOptions();
    const salariedMadeAvailableTimeOptions = getDeductionsConfigOptions({
      exclude: ['EARNINGS_PAY_CYCLE_START'],
    });

    useEffect(() => {
      trigger();
    }, [trigger]);

    const resetAndFocusField = (field: keyof PayCalendarAslanDetailsFormFields) => {
      setValue(field, undefined, { shouldValidate: true, shouldDirty: true });
      setFocus(field);
    };

    const handleNextStep = (data: PayCalendarAslanDetailsFormFields) => {
      setPayCalendarData(data);
      onStepAction();
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      setPayCalendarData(formValues);
      previousStep();
    };

    return (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          {isReserveEnabled && (
            <FormControl isInvalid={!!errors?.globalAllowancePercentage?.message} isRequired>
              <FormLabel>Global limit (%)</FormLabel>
              <Controller
                control={control}
                name="globalAllowancePercentage"
                render={({ field: { onChange, onBlur, value } }) => (
                  <DropdownInput
                    placeholder="Users can set and spend up to this limit"
                    defaultValue={globalAllowancePercentageOptions.find(
                      (option) => option.value === value
                    )}
                    options={globalAllowancePercentageOptions}
                    onChange={(val) => {
                      onChange(val);
                      if (
                        getValues('odpAllowancePercentage') !== undefined &&
                        getValues('odpAllowancePercentage') !== null
                      ) {
                        trigger('odpAllowancePercentage');
                      }
                      if (
                        getValues('customOdpAllowancePercentage') !== undefined &&
                        getValues('customOdpAllowancePercentage') !== null
                      ) {
                        trigger('customOdpAllowancePercentage');
                      }
                    }}
                    onBlur={onBlur}
                  />
                )}
              />
              <FormErrorMessage>{errors?.globalAllowancePercentage?.message}</FormErrorMessage>
            </FormControl>
          )}
          {isReserveEnabled && watchGlobalAllowancePercentage === 200 && (
            <FormControl isInvalid={!!errors?.customGlobalAllowancePercentage?.message} isRequired>
              <FormLabel>What custom global limit percentage would you like?</FormLabel>
              <InputGroup>
                <InputLeftAddon>%</InputLeftAddon>
                <Input
                  type="number"
                  step="1"
                  placeholder="Enter percentage from 1 to 100"
                  isInvalid={!!errors?.customGlobalAllowancePercentage?.message}
                  onKeyDown={(e) =>
                    ['.', ',', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
                  }
                  {...register('customGlobalAllowancePercentage')}
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Clear custom global limit percentage"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('customGlobalAllowancePercentage')}
                  />
                </InputRightElement>
              </InputGroup>

              <FormErrorMessage>
                {errors?.customGlobalAllowancePercentage?.message}
              </FormErrorMessage>

              {/** Warning Message for High Percentage */}
              {watchCustomGlobalAllowancePercentage !== undefined &&
                watchCustomGlobalAllowancePercentage > 50 &&
                watchCustomGlobalAllowancePercentage <= 100 && (
                  <Text color="red" size="8" fontSize="sm" mt="2">
                    <strong>Warning:</strong> This is a high percentage, please be sure.
                  </Text>
                )}
            </FormControl>
          )}
          {isOnDemandPayEnabled && (
            <FormControl isInvalid={!!errors?.odpAllowancePercentage?.message} isRequired>
              <FormLabel>On-demand pay sub-limit (%)</FormLabel>
              <Controller
                control={control}
                name="odpAllowancePercentage"
                render={({ field: { onChange, onBlur, value } }) => (
                  <DropdownInput
                    placeholder="Users can access ODP up to this limit"
                    defaultValue={odpAllowancePercentageOptions.find(
                      (option) => option.value === value
                    )}
                    options={odpAllowancePercentageOptions}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <FormErrorMessage>{errors?.odpAllowancePercentage?.message}</FormErrorMessage>
            </FormControl>
          )}
          {isOnDemandPayEnabled && watchOdpAllowancePercentage === 200 && (
            <FormControl isInvalid={!!errors?.customOdpAllowancePercentage?.message} isRequired>
              <FormLabel>What custom percentage would you like?</FormLabel>
              <InputGroup>
                <InputLeftAddon>%</InputLeftAddon>
                <Input
                  type="number"
                  placeholder="Enter percentage from 0 to 100"
                  {...register('customOdpAllowancePercentage')}
                />
                <InputRightElement>
                  <IconButton
                    aria-label="Clear custom percentage"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('customOdpAllowancePercentage')}
                  />
                </InputRightElement>
              </InputGroup>

              <FormErrorMessage>{errors?.customOdpAllowancePercentage?.message}</FormErrorMessage>

              {/** Warning Message for High Percentage */}
              {watchCustomOdpAllowancePercentage !== undefined &&
                watchCustomOdpAllowancePercentage > 50 && (
                  <Text color="red" size="8" fontSize="sm" mt="2">
                    <strong>Warning:</strong> This is a high percentage, please be sure.
                  </Text>
                )}
              {/** Warning Message for Zero Percentage */}
              {watchCustomOdpAllowancePercentage !== undefined &&
                String(watchCustomOdpAllowancePercentage).trim() !== '' &&
                Number(watchCustomOdpAllowancePercentage) === 0 && (
                  <Text color="red" size="8" fontSize="sm" mt="2">
                    <strong>Warning:</strong> This is a zero (0) percentage, please be sure.
                  </Text>
                )}
            </FormControl>
          )}

          <FormControl isInvalid={!!errors?.accrualPeriod?.message} isRequired>
            <FormLabel>Accrual period</FormLabel>
            <Controller
              control={control}
              name="accrualPeriod"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Set how accruals will run for this pay calendar"
                  defaultValue={accrualPeriodOptions.find((option) => option.value === value)}
                  options={accrualPeriodOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.accrualPeriod?.message}</FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={!!errors?.salariedDeductionsRequestMadeAvailableConfig?.message}
            isRequired
          >
            <FormLabel>Made Available Time (MAT) for salaried workers</FormLabel>
            <Controller
              control={control}
              name="salariedDeductionsRequestMadeAvailableConfig"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="When is set & spent made available?"
                  defaultValue={salariedMadeAvailableTimeOptions.find(
                    (option) => option.value === value
                  )}
                  options={salariedMadeAvailableTimeOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>
              {errors?.salariedDeductionsRequestMadeAvailableConfig?.message || ''}
            </FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={!!errors?.nonSalariedDeductionsRequestMadeAvailableConfig?.message}
            isRequired
          >
            <FormLabel>Made Available Time (MAT) for non-salaried workers</FormLabel>
            <Controller
              control={control}
              name="nonSalariedDeductionsRequestMadeAvailableConfig"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="When is set & spent made available?"
                  defaultValue={madeAvailableTimeOptions.find((option) => option.value === value)}
                  options={madeAvailableTimeOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>
              {errors?.nonSalariedDeductionsRequestMadeAvailableConfig?.message || ''}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.startingBalanceDays?.message} isRequired>
            <FormLabel>1st of the month accrual (working days)</FormLabel>
            <Controller
              control={control}
              name="startingBalanceDays"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="The users starting balance on the 1st of the month"
                  defaultValue={startingBalanceDaysOptions.find((option) => option.value === value)}
                  options={startingBalanceDaysOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.startingBalanceDays?.message}</FormErrorMessage>
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<CheckOutline boxSize="20px" />}
            >
              Create
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

PayCalendarAslanDetailsForm.displayName = 'PayCalendarAslanDetailsForm';

export default PayCalendarAslanDetailsForm;
